import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuthUser } from '../../context/authHooks';
import { apiClient } from '../../shared/api/apiClient';

// Fase 5.18 (05-remediation.md, parte 2) -- `react-hooks/set-state-in-effect` (hint de React
// Compiler, no un bug de runtime) sube a `error` en todo `src/`. Este archivo hace setState
// síncrono dentro de un `useEffect` en un patrón de sincronización legítimo: fetch inicial de calendarios (dispara la mutación de red, no un setState directo) + selector de fetch de miembros por espacio vs. global.
// No se reescribe a estado derivado (repetiría el fetch/haría el UI menos predecible en
// varios de estos casos) -- deshabilitado a nivel de archivo, con motivo, no en silencio.
/* eslint-disable react-hooks/set-state-in-effect */
const CalendarContext = createContext({
    calendars: [],
    visibleCalendarIds: new Set(),
    toggleCalendar: () => { },
    isCalendarVisible: () => true,
    refreshCalendars: () => { },
    showCalendly: true,
    showTaggedEvents: true,
    showTasks: true,
    setShowCalendly: () => {},
    setShowTaggedEvents: () => {},
    setShowTasks: () => {},
    otherUsersState: {},
    toggleOtherUserMain: () => {},
    toggleOtherUserSub: () => {},
    selectedUserIds: new Set(),
    setSelectedUserIds: () => {},
    members: [],
    setMembers: () => {},
    loadingMembers: false,
    createCalendar: () => {},
    updateCalendar: () => {},
    deleteCalendar: () => {}
});

export const useCalendar = () => useContext(CalendarContext);

export const CalendarProvider = ({ children }) => {
    const user = useAuthUser();
    const [calendars, setCalendars] = useState([]);
    const [visibleCalendarIds, setVisibleCalendarIds] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [showCalendly, setShowCalendly] = useState(true);
    const [showTaggedEvents, setShowTaggedEvents] = useState(true);
    const [showTasks, setShowTasks] = useState(true);
    const [otherUsersState, setOtherUsersState] = useState({});
    const [selectedUserIds, setSelectedUserIds] = useState(new Set());
    const [members, setMembers] = useState([]);

    // Toggle main checkbox for another user
    const toggleOtherUserMain = useCallback((userId) => {
        setOtherUsersState(prev => {
            const current = prev[userId] || { mainVisible: true, showCalendly: true, showTaggedEvents: true, showTasks: true };
            const newState = !current.mainVisible;
            return {
                ...prev,
                [userId]: {
                    mainVisible: newState,
                    showCalendly: newState,
                    showTaggedEvents: newState,
                    showTasks: newState
                }
            };
        });
    }, []);

    // Toggle sub-item for another user
    const toggleOtherUserSub = useCallback((userId, subItem) => {
        setOtherUsersState(prev => {
            const current = prev[userId] || { mainVisible: true, showCalendly: true, showTaggedEvents: true, showTasks: true };
            return {
                ...prev,
                [userId]: {
                    ...current,
                    [subItem]: !current[subItem]
                }
            };
        });
    }, []);

    // 02.5-calendar.md §0.6/§4 "a.4" -- fetch propio en vez de `useCalendars()` (React Query,
    // `hooks/useCalendarData.js`), que `ComponentCalendar.jsx` sí usa: decisión deliberada, no un
    // descuido. Unificar acá arrastraría también `side-calendar.jsx`, que hand-muta `calendars`
    // vía `setCalendars` (su propio CRUD de calendarios -- crear/renombrar/borrar un calendario,
    // no de eventos) en 3 sitios -- un mirror análogo al que retiró `a.2`, pero en un archivo ya
    // agendado para su propio split presentacional (paso `b`/§5). Retirarlo ahora sería relitigar
    // ese archivo dos veces. Queda para cuando `side-calendar.jsx` se toque en `b`.
    const fetchCalendars = useCallback(async () => {
        if (!user?.dbId) return;
        setLoading(true);
        try {
            const data = await apiClient.get(`/api/calendars?userId=${user.dbId}`);
            if (data.success) {
                const cals = data.calendars;
                // Maintain established Sort: Personal (Owner) First
                cals.sort((a, b) => {
                    if (user?.calendar_personal && a.id === user.calendar_personal) return -1;
                    if (user?.calendar_personal && b.id === user.calendar_personal) return 1;

                    const aIsP = a.name === 'Personal' && String(a.user_id) === String(user.dbId);
                    const bIsP = b.name === 'Personal' && String(b.user_id) === String(user.dbId);
                    if (aIsP) return -1;
                    if (bIsP) return 1;
                    return 0;
                });

                setCalendars(cals);

                // Initialize all as visible on first load
                // We use function update to avoid resetting if we have state, 
                // BUT if we reload the page, state is lost anyway.
                // If we navigate away and back, state might be preserved if Context is high up.
                setVisibleCalendarIds(prev => {
                    if (prev.size > 0) return prev;
                    return new Set(cals.map(c => c.id));
                });
            }
        } catch (e) {
            console.error("Context: Error fetching calendars", e);
        } finally {
            setLoading(false);
        }
    // Dependencia en `user` completo, no en campos puntuales (`user?.dbId`,
    // `user?.calendar_personal`): el cuerpo los lee tanto con optional chaining como sin él
    // dentro del comparador anidado de `.sort()`, lo que confunde la inferencia de dependencias
    // del compilador de React (regla `react-hooks/preserve-manual-memoization` de
    // `eslint-plugin-react-hooks@7` -- ver "Compilation Skipped" en el lint) y termina
    // igual infiriendo `user` como dependencia real. Mismo criterio que ya usa
    // `UserContext.jsx` para su propio `contextValue`.
    }, [user]);

    // Perf (fase 43.2): antes dependía de `[user?.dbId]` directo; ahora que `fetchCalendars`
    // está memoizada con `useCallback`, el lint (`react-hooks/exhaustive-deps`) exige que el
    // efecto dependa de la función, no de la variable cruda. Es equivalente en comportamiento
    // -- `fetchCalendars` ya depende de `user?.dbId` y `user?.calendar_personal` -- y corrige de
    // paso un caso borde: si `calendar_personal` cambiara sin que cambiara `dbId`, antes el
    // efecto no se disparaba de nuevo y `fetchCalendars` quedaba con un valor stale de esa
    // dependencia en su closure.
    useEffect(() => {
        fetchCalendars();
    }, [fetchCalendars]);

    // Fase 5.13 -- `side-calendar.jsx` (presentacional) hacía su propio CRUD de calendarios
    // (crear/renombrar/borrar) y el fetch de la lista de personas vía `apiClient` directo, con
    // mutación optimista sobre `setCalendars`/`setMembers`. Se sube acá (el provider ya es dueño
    // de ese estado). El comentario de `fetchCalendars` arriba anticipaba justo esta mudanza.
    const [loadingMembers, setLoadingMembers] = useState(false);

    const fetchAllUsers = useCallback(async () => {
        try {
            setLoadingMembers(true);
            const data = await apiClient.get('/api/users?context=calendar');
            if (Array.isArray(data)) {
                const mapped = data.map((u) => ({
                    id: u.id,
                    name: u.nick,
                    email: u.email,
                    image: (u['link-profile-img-thumb'] || u['link-profile-img']),
                }));
                setMembers(mapped);
                if (user?.dbId) setSelectedUserIds(new Set([String(user.dbId)]));
            }
        } catch (error) {
            console.error('Calendar: failed to fetch all users', error);
        } finally {
            setLoadingMembers(false);
        }
    }, [user?.dbId]);

    const fetchMembers = useCallback(async (spaceId) => {
        try {
            setLoadingMembers(true);
            const data = await apiClient.get(`/api/space/${spaceId}/members`);
            if (data.success && Array.isArray(data.members)) {
                setMembers(data.members);
                if (user?.dbId) setSelectedUserIds(new Set([String(user.dbId)]));
            } else {
                fetchAllUsers();
            }
        } catch (error) {
            console.error('Calendar: failed to fetch space members', error);
            fetchAllUsers();
        } finally {
            setLoadingMembers(false);
        }
    }, [user?.dbId, fetchAllUsers]);

    useEffect(() => {
        if (user?.currentSpace?.id) fetchMembers(user.currentSpace.id);
        else fetchAllUsers();
    }, [user?.currentSpace?.id, fetchMembers, fetchAllUsers]);

    const updateCalendar = useCallback(async (updatedCal) => {
        setCalendars((prev) => prev.map((c) => (c.id === updatedCal.id ? updatedCal : c)));
        try {
            await apiClient.put(`/api/calendars/${updatedCal.id}`, {
                name: updatedCal.name || updatedCal.title,
                color: updatedCal.color,
            });
        } catch (e) { console.error('Calendar: update failed', e); }
    }, []);

    const deleteCalendar = useCallback(async (calId) => {
        setCalendars((prev) => prev.filter((c) => c.id !== calId));
        try {
            await apiClient.del(`/api/calendars/${calId}`);
        } catch (e) { console.error('Calendar: delete failed', e); }
    }, []);

    const createCalendar = useCallback(async (name) => {
        if (!user?.dbId) return;
        try {
            const data = await apiClient.post('/api/calendars', {
                name,
                color: '#8b5cf6',
                user_id: user.dbId,
            });
            if (data.success) {
                setCalendars((prev) => [...prev, {
                    id: data.calendar.id,
                    title: data.calendar.name,
                    color: data.calendar.color,
                    checked: true,
                    owner_id: user.dbId,
                }]);
            }
        } catch (e) { console.error('Calendar: create failed', e); }
    }, [user?.dbId]);

    const toggleCalendar = useCallback((id) => {
        setVisibleCalendarIds(prev => {
            const next = new Set(prev);

            // Type-agnostic check for deletion
            let isVisible = false;
            let foundId = id;
            if (next.has(id)) { isVisible = true; foundId = id; }
            else if (next.has(String(id))) { isVisible = true; foundId = String(id); }
            else if (next.has(Number(id))) { isVisible = true; foundId = Number(id); }

            if (isVisible) next.delete(foundId);
            else next.add(id);

            // If toggling the personal calendar, sync the sub-items
            if (user?.calendar_personal === id || String(user?.calendar_personal) === String(id)) {
                const newState = !isVisible;
                setShowCalendly(newState);
                setShowTaggedEvents(newState);
                setShowTasks(newState);
            }

            return next;
        });
    }, [user?.calendar_personal]);

    const isCalendarVisible = useCallback((id) => {
        if (!visibleCalendarIds) return false;
        return visibleCalendarIds.has(id) || visibleCalendarIds.has(String(id)) || visibleCalendarIds.has(Number(id));
    }, [visibleCalendarIds]);

    // Perf (fase 43.2): antes este objeto se recreaba en cada render de `CalendarProvider` --
    // mismo bug ya corregido en `UserContext.jsx`. Requiere que las funciones que expone (arriba)
    // ya estén estabilizadas con `useCallback`, si no este `useMemo` igual invalidaría en cada
    // render (una de sus dependencias -- la función -- cambiaría de referencia todo el tiempo).
    const contextValue = useMemo(() => ({
        calendars,
        setCalendars,
        visibleCalendarIds,
        toggleCalendar,
        isCalendarVisible,
        refreshCalendars: fetchCalendars,
        loading,
        showCalendly, setShowCalendly,
        showTaggedEvents, setShowTaggedEvents,
        showTasks, setShowTasks,
        otherUsersState,
        toggleOtherUserMain,
        toggleOtherUserSub,
        selectedUserIds,
        setSelectedUserIds,
        members,
        setMembers,
        loadingMembers,
        createCalendar,
        updateCalendar,
        deleteCalendar
    }), [
        calendars,
        setCalendars,
        visibleCalendarIds,
        toggleCalendar,
        isCalendarVisible,
        fetchCalendars,
        loading,
        showCalendly, setShowCalendly,
        showTaggedEvents, setShowTaggedEvents,
        showTasks, setShowTasks,
        otherUsersState,
        toggleOtherUserMain,
        toggleOtherUserSub,
        selectedUserIds,
        setSelectedUserIds,
        members,
        setMembers,
        loadingMembers,
        createCalendar,
        updateCalendar,
        deleteCalendar
    ]);

    return (
        <CalendarContext.Provider value={contextValue}>
            {children}
        </CalendarContext.Provider>
    );
};
